import { useState, useEffect, useRef, useMemo, memo } from "react";
import { RiSendPlaneFill } from "react-icons/ri";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import { InputBase, useMediaQuery } from "@material-ui/core";
import SwAlert from "../components/SwAlert";
import LoadingDots from "../components/LoadingDots";
import { getChatData, getLatestChatMessages, sendChatMessageStream } from "../crud/chat.crud";
import { LayoutSplashScreen } from "../../_metronic";
import { FileIcon, formatText, useAutoScroll } from "../utils/ChatUtils";
import { setFavicon } from "../utils/Utils";

const sessionKey = "chatData";

const Chat = ({ iFrame = false }) => {
    const { uuid } = useParams();
    const queryParams = new URLSearchParams(window.location.search);
    const intervalRef = useRef(null);

    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        id: null,
        cha_nome: "Nova conversa",
        agt_name: "",
        agt_text_color: "",
        agt_chat_color: "",
        agt_button_color: "",
        agt_background_color: "",
        picture: "",
        messages: [],
    });

    useEffect(() => {
        const session = sessionStorage.getItem(sessionKey);

        if (session) {
            const parsedSession = JSON.parse(session);
            if (parsedSession.id) setData(parsedSession);
        }
    }, []);

    useEffect(() => {
        setLoading(true);

        getChatData(uuid, queryParams.get("tk"))
            .then((res) => {
                setData((d) => {
                    const newData = { ...d, ...res.data };
                    sessionStorage.setItem(sessionKey, JSON.stringify(newData));

                    return newData;
                });
                document.title = res.data.agt_name;
                setFavicon(res.data.picture);
            })
            .catch(() => {
                setError(true);
            })
            .finally(() => {
                setLoading(false);
                document.getElementById("messagesContainer")?.scrollTo({ top: 9999999, behavior: "instant" });
            });
    }, [uuid]);

    if (iFrame) {
        return <FrameChat data={data} setData={setData} error={error} loading={loading} uuid={uuid} queryParams={queryParams} iFrame intervalRef={intervalRef} />;
    }

    return (
        <div className="ai-chat-container w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: `#${data.agt_background_color}` }}>
            <FrameChat data={data} setData={setData} error={error} loading={loading} uuid={uuid} queryParams={queryParams} intervalRef={intervalRef} />
        </div>
    );
};

const FrameChat = ({ data, setData, error, loading, uuid, queryParams, iFrame, intervalRef }) => {
    const messagesContainerRef = useRef();
    const inputContainerRef = useRef();
    const agentRef = useRef();
    const isMobileScreen = useMediaQuery("(max-width: 576px)");

    const [submitting, setSubmitting] = useState(false);
    const [endStream, setEndStream] = useState(true);
    const [streamingMessage, setStreamingMessage] = useState(null);
    const [refreshInputHeight, setRefreshInputHeight] = useState(false);
    const [inputContainerHeight, setInputContainerHeight] = useState(0);

    useEffect(() => {
        const handleResize = () => {
            if (inputContainerRef.current) {
                setInputContainerHeight(inputContainerRef.current.offsetHeight);
            }
        };

        window.visualViewport?.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.visualViewport?.removeEventListener("resize", handleResize);
        };
    }, [refreshInputHeight]);

    useEffect(() => {
        agentRef.current = data;

        if (data?.id && !intervalRef.current) {
            intervalRef.current = setInterval(() => getLatestMessages(agentRef.current), 10000);
        }
    }, [data]);

    const getLatestMessages = async (agent) => {
        if (!agent) return;

        const tempMessages = agent.messages ?? [];

        try {
            const lastMessageId = tempMessages.filter((m) => m.id).at(-1)?.id ?? "";

            const res = await getLatestChatMessages(uuid, agent.id, queryParams.get("tk"), lastMessageId);

            if (Array.isArray(res.data) && res.data.length) {
                const newMessages = res.data.map((msg) => ({
                    id: msg.id_message,
                    chm_tipo_autor: msg.type,
                    chm_mensagem: msg.message,
                    fileResponse: msg.fileResponse,
                }));

                const messageMap = new Map();

                for (const m of tempMessages) {
                    messageMap.set(m.id, m);
                }

                for (const m of newMessages) {
                    messageMap.set(m.id, m);
                }

                const mergedMessages = Array.from(messageMap.values());

                const isDifferent = mergedMessages.length !== tempMessages.length || mergedMessages.some((m, i) => m.id !== tempMessages[i]?.id || m.chm_mensagem !== tempMessages[i]?.chm_mensagem);

                if (isDifferent) {
                    const newData = { ...agent, messages: mergedMessages };

                    setData(newData);
                    document.getElementById("messagesContainer")?.scrollTo({ top: 9999999, behavior: "instant" });
                    sessionStorage.setItem(sessionKey, JSON.stringify(newData));
                }

                if (intervalRef.current) clearInterval(intervalRef.current);
                intervalRef.current = setInterval(() => getLatestMessages(agentRef.current), 10000);
            }
        } catch {}
    };

    useEffect(() => {
        if (!endStream && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        } else if (data?.id && agentRef.current) {
            intervalRef.current = setInterval(() => getLatestMessages(agentRef.current), 10000);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [endStream]);

    /**
     * Força scroll para a mensagem do chat enviada ou recebida
     */
    useAutoScroll(messagesContainerRef, [submitting, endStream, streamingMessage, inputContainerHeight]);

    const onSubmit = (values, { resetForm, setFieldValue }) => {
        if (values.mensagem && !submitting) {
            setData((d) => ({ ...d, messages: [...d.messages, { chm_tipo_autor: "user", chm_mensagem: values.mensagem }] }));
            setSubmitting(true);
            setEndStream(false);
            resetForm();

            let responseMessage = "";

            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            sendChatMessageStream(
                uuid,
                {
                    id_chat: data.id,
                    message: values.mensagem,
                },
                queryParams.get("tk"),
                (chunk) => {
                    setSubmitting(false);
                    responseMessage += chunk;

                    setStreamingMessage(responseMessage);
                },
                (completed) => {
                    /**
                     * id_message - UUID da mensagem
                     * id_chat - UUID do chat
                     * type - Autor da mensagem (user|assistant|whatsapp-assistant-user|app-assistant-user|file)
                     * message - Mensagem de resposta
                     * chat_name - Nome do chat
                     */
                    const { id_message, id_chat, type, message, chat_name, file_message, userMessage } = completed;

                    if (id_message || userMessage) {
                        setData((d) => {
                            const newData = {
                                ...d,
                                id: id_chat,
                                cha_nome: chat_name,
                                messages: [
                                    ...d.messages.filter((m) => m.id),
                                    ...(userMessage
                                        ? [
                                              {
                                                  id: userMessage.id_message,
                                                  chm_tipo_autor: userMessage.type,
                                                  chm_mensagem: userMessage.message,
                                              },
                                          ]
                                        : []),
                                    ...(id_message
                                        ? [
                                              {
                                                  id: id_message,
                                                  chm_tipo_autor: type,
                                                  chm_mensagem: message,
                                              },
                                          ]
                                        : []),
                                    ...(file_message ? [{ ...file_message, id: file_message.id_message, chm_tipo_autor: file_message.type, chm_mensagem: file_message.message }] : []),
                                ],
                            };

                            sessionStorage.setItem(sessionKey, JSON.stringify(newData));

                            return newData;
                        });
                    }

                    setEndStream(true);
                    setStreamingMessage(null);
                    setSubmitting(false);
                },
                (err) => {
                    setSubmitting(false);
                    SwAlert({
                        title: typeof err === "string" ? err : `Parece que houve um problema`,
                        text: typeof err === "string" ? null : "Por favor, entre em contato com o suporte.",
                        customClass: {
                            content: typeof err === "string" ? "p-0 pb-32" : "",
                        },
                        confirmButtonText: "Fechar",
                        icon: "error",
                    });
                    setFieldValue("mensagem", values.mensagem);
                    setEndStream(true);
                    setStreamingMessage(null);
                },
            );
        }
    };

    const Salutation = (d) => `${d.agt_name}`;

    const ChatHeader = () => {
        return data.messages.length ? (
            <div className={`w-100 d-flex align-items-center gap-24 pbl-24 ${isMobileScreen ? "pil-24" : "pil-40"}`} style={{ borderBottom: `1px solid #${data.agt_background_color}`, padding: isMobileScreen && iFrame ? 16 : undefined }}>
                <div className="agentesAvatar" style={{ backgroundImage: `url(${data.picture})`, width: 40, minWidth: 40, minHeight: 40, height: 40 }} />

                <div className="d-flex flex-column">
                    <span className={`${isMobileScreen ? "tt-18" : "tt-20"} medium`} style={{ color: `#${data.agt_text_color}` }}>
                        {data.agt_name}
                    </span>
                </div>
            </div>
        ) : (
            <div className="w-100 p-40 d-flex justify-content-center" style={{ height: `calc(100% - ${inputContainerHeight}px)`, maxHeight: `calc(100% - ${inputContainerHeight}px)`, overflow: "auto" }}>
                <div className="w-100 d-flex flex-column align-items-center gap-16" style={{ maxWidth: isMobileScreen ? "100%" : "80%" }}>
                    <div className="agentesAvatar" style={{ backgroundImage: `url(${data.picture})` }} />

                    <div className="d-flex flex-column gap-16 align-items-center text-center">
                        <span className={`${isMobileScreen ? "tt-18" : "tt-20"} semibold`} style={{ color: `#${data.agt_text_color}` }}>
                            {Salutation(data)}
                        </span>

                        {data.agt_presentation ? (
                            <span className={isMobileScreen ? "tt-14" : "tt-16"} style={{ color: `#${data.agt_text_color}`, opacity: "70%", whiteSpace: "preserve" }}>
                                {data.agt_presentation}
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    const getContrastColor = (hexColor) => {
        // Remove the hash if present
        const color = hexColor.replace("#", "");
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);

        // Calculate luminance
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

        // Return black for light colors and white for dark colors
        return luminance > 186 ? "#111928" : "#FFFFFF";
    };

    const MessageFromAI = memo(({ message, agt_text_color, autor }) => (
        <div className="ai-chat-message-from-ai">
            {AgentAvatar}

            <div className={`ai-chat-message-from-ai-message-container ${isMobileScreen && iFrame ? "tt-14" : "tt-16"}`} style={{ color: `#${agt_text_color}`, whiteSpace: ["whatsapp-assistant-user", "app-assistant-user"].includes(message.chm_tipo_autor) ? "pre-wrap" : undefined }}>
                {formatText(message.chm_mensagem)}
            </div>
        </div>
    ));

    const FileMessage = memo(({ message, agt_text_color, agt_background_color, agt_button_color }) => {
        const fileName = message.fileResponse?.fil_original_name ?? message.fileResponse?.fil_display_name ?? message.fileResponse?.fil_name;
        const fileType = message.fileResponse?.fil_type;
        const fileUrl = message.fileResponse?.url;

        return (
            <div className="ai-chat-message-from-ai">
                {AgentAvatar}

                <div className={`ai-chat-message-from-ai-message-container`} style={{ cursor: "pointer" }} onClick={() => window.open(fileUrl, "_blank")}>
                    {fileType.startsWith("image/") ? (
                        <img className="image-container" src={fileUrl} alt="file" />
                    ) : (
                        <div className="d-flex align-items-center gap-10 message-files" style={{ backgroundColor: `#${agt_background_color}` }}>
                            <div className="files-icon">
                                <FileIcon fileType={fileType} color={agt_button_color} />
                            </div>
                            <span title={fileName} className={`message-files-filename ${isMobileScreen && iFrame ? "tt-14" : "tt-16"}`} style={{ color: `#${agt_text_color}` }}>
                                {fileName}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        );
    });

    const MessageFromUser = memo(({ message, agt_background_color, agt_text_color, isMobileScreen, iFrame }) => (
        <div className={`ai-chat-message-from-user ${isMobileScreen && iFrame ? "tt-14" : "tt-16"}`} style={{ background: `#${agt_background_color}` }}>
            <span style={{ color: `#${agt_text_color}`, whiteSpace: "pre-wrap" }}>{message.chm_mensagem}</span>
        </div>
    ));

    const AgentAvatar = useMemo(() => <div className="ai-chat-message-from-ai-avatar" style={{ backgroundImage: `url(${data.picture})` }} />, [data.picture]);

    const messagesList = useMemo(
        () =>
            Boolean(data.messages.length) && (
                <>
                    {data.agt_presentation && (
                        <div key={`ai-msg-pres`} className="ai-chat-message-from-ai">
                            {AgentAvatar}
                            <div className="ai-chat-message-from-ai-message-container">
                                <span className={isMobileScreen && iFrame ? "tt-14" : "tt-16"} style={{ color: `#${data.agt_text_color}`, whiteSpace: "preserve" }}>
                                    {data.agt_presentation}
                                </span>
                            </div>
                        </div>
                    )}
                    {data.messages.map(
                        (message, idx) =>
                            (["assistant", "whatsapp-assistant-user", "app-assistant-user"].includes(message.chm_tipo_autor) && <MessageFromAI key={`ai-msg-${idx}`} message={message} agt_text_color={data.agt_text_color} autor={message.chm_tipo_autor} />) ||
                            (message.chm_tipo_autor === "file" && message.fileResponse && <FileMessage key={`file-msg-${idx}`} message={message} agt_text_color={data.agt_text_color} agt_background_color={data.agt_background_color} agt_button_color={data.agt_button_color} />) || (
                                <MessageFromUser key={`usr-msg-${idx}`} message={message} agt_background_color={data.agt_background_color} agt_text_color={data.agt_text_color} isMobileScreen={isMobileScreen} iFrame={iFrame} />
                            ),
                    )}
                </>
            ),
        [data.messages, data.agt_presentation, data.agt_text_color, data.agt_background_color, isMobileScreen, iFrame, AgentAvatar],
    );

    // Memoize streaming message so only this part re-renders on every chunk
    const streamingMessageMemo = useMemo(() => (streamingMessage ? <MessageFromAI message={{ chm_mensagem: streamingMessage }} agt_text_color={data.agt_text_color} /> : null), [streamingMessage, data.agt_text_color, AgentAvatar]);

    const loadingDotsMemo = useMemo(
        () =>
            submitting ? (
                <div className="ai-chat-message-from-ai">
                    {AgentAvatar}
                    <div className="ai-chat-message-from-ai-message-container">
                        <LoadingDots
                            style={{
                                backgroundColor: `#${data.agt_text_color}`,
                                opacity: "70%",
                            }}
                        />
                    </div>
                </div>
            ) : null,
        [submitting, data.agt_text_color, AgentAvatar],
    );

    if (loading) {
        return <LayoutSplashScreen visible />;
    }

    if (error) {
        return (
            <div className="authContainer h-100 justify-content-center gap-40">
                <img alt="oliverai" src="/images/logo-white.png" width={250} />

                <span className="tt-24 color-white">Chat não encontrado</span>
            </div>
        );
    }

    return (
        <div className="ai-chat-container-content" style={{ background: `#${data.agt_chat_color}`, width: iFrame ? "100dvw" : undefined, maxWidth: iFrame ? "100dvw" : undefined, height: iFrame ? "100dvh" : undefined, borderRadius: iFrame ? "unset" : undefined }}>
            <ChatHeader />

            <style>
                {`
						body {
							background: #${data.agt_background_color} !important;
							overflow: hidden;
						}

						.ai-chat-container-content {
							::-webkit-scrollbar-thumb {
								background: #${data.agt_background_color} !important;
								border: 0px none #${data.agt_background_color} !important;
							}

							::-webkit-scrollbar {
								background: #${data.agt_chat_color} !important;
							}

							::-webkit-scrollbar-track {
								background: transparent !important;
								border: 0px !important;
							}

							.ai-chat-message-from-ai-message-container .message pre {
								padding: 8px;
								background-color: #${data.agt_background_color};
								color: #${data.agt_text_color};
								font-size: 12px;
								line-height: 18px;
								border-radius: 8px;

								code {
									background-color: transparent;
								}
							}
						}

					`}
            </style>

            <div className="ai-chat-body" style={{ height: data.messages.length ? undefined : inputContainerHeight }}>
                <div className={`ai-chat ${data.messages.length ? "" : "newChat"}`}>
                    <div id="messagesContainer" ref={messagesContainerRef} className={`ai-chat-messages-container ${data.messages.length ? "" : "newChat"}`} style={{ height: `calc100%`, maxHeight: `calc(100% - ${inputContainerHeight}px)` }}>
                        {messagesList}
                        {streamingMessageMemo}
                        {loadingDotsMemo}
                    </div>

                    <div ref={inputContainerRef} className={`ai-chat-input-container ${data.messages.length ? "" : "newChat"}`}>
                        <Formik
                            initialValues={{ mensagem: "" }}
                            onSubmit={onSubmit}
                            validate={() => {
                                setTimeout(() => {
                                    setRefreshInputHeight(!refreshInputHeight);
                                }, 100);
                            }}
                        >
                            {({ values, handleChange, handleSubmit }) => (
                                <form noValidate autoComplete="off" onSubmit={handleSubmit} className={`ai-chat-input-text ${isMobileScreen && iFrame ? "p-8" : ""}`} style={{ background: `#${data.agt_background_color}` }}>
                                    <InputBase
                                        multiline
                                        name="mensagem"
                                        minRows={2}
                                        maxRows={10}
                                        value={values.mensagem}
                                        placeholder="Escreva aqui o que deseja saber"
                                        onChange={handleChange}
                                        style={{ flex: 1 }}
                                        className="ai-chat-input"
                                        inputProps={{
                                            style: {
                                                fontWeight: 400,
                                                lineHeight: "24px",
                                                fontSize: isMobileScreen && iFrame ? 12 : 16,
                                                padding: "8px 0px",
                                                color: `#${data.agt_text_color}`,
                                                "::placeholder": {
                                                    color: `#${data.agt_text_color}`,
                                                    opacity: 0.5,
                                                },
                                            },
                                            maxLength: 3000,
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.shiftKey || event.ctrlKey) {
                                                return;
                                            }

                                            if (event.key === "Enter") {
                                                event.preventDefault();

                                                if (!(submitting || !endStream || !values.mensagem)) {
                                                    handleSubmit();
                                                }
                                            }
                                        }}
                                    />

                                    <button disabled={submitting || !endStream || !values.mensagem} style={{ background: `#${data.agt_button_color}` }} className="ai-chat-submit-button" type="submit">
                                        <RiSendPlaneFill size={24} style={{ color: getContrastColor(`#${data.agt_button_color}`) }} />
                                    </button>
                                </form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Chat;
